import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ProfileStatusCardProps {
  profileCompleted: boolean;
  isActive: boolean;
  userId: string;
  onActiveToggle: (active: boolean) => void;
}

export function ProfileStatusCard({ profileCompleted, isActive, userId, onActiveToggle }: ProfileStatusCardProps) {
  const navigate = useNavigate();
  const completionPercentage = profileCompleted ? 100 : 50;

  const handleActiveToggle = async (checked: boolean) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ is_active: checked })
        .eq("id", userId);

      if (error) throw error;
      
      onActiveToggle(checked);
      toast.success(checked ? "Profile activated" : "Profile deactivated");
    } catch (error) {
      console.error("profile.status_toggle_failed");
      toast.error("Failed to update profile status");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Profile Status</CardTitle>
          <Badge variant={isActive ? "default" : "secondary"}>
            {isActive ? "Active" : "Inactive"}
          </Badge>
        </div>
        <CardDescription>
          Your profile completion and visibility
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Profile Completion</span>
            <span className="font-medium">{completionPercentage}%</span>
          </div>
          <Progress value={completionPercentage} />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="active-toggle" className="cursor-pointer">
            Profile Visibility
          </Label>
          <Switch
            id="active-toggle"
            checked={isActive}
            onCheckedChange={handleActiveToggle}
          />
        </div>

        <Button 
          onClick={() => navigate("/profile/create")}
          className="w-full"
          variant="outline"
        >
          Edit Profile
        </Button>
      </CardContent>
    </Card>
  );
}
